import React from 'react';
import { capitalize, formatDateStrWithYearShort } from 'utils';
import type { JsonObject } from '@/app-package';
import { ListingDoc } from '@bb/domain/dist/schemas';
import { Text } from 'components';

import { ListingLabelContext } from 'contexts';
import { formatPrice } from '../../helpers';

type ListingDetailsProps = {
  listing: JsonObject<Omit<ListingDoc, 'org' | 'reps'>>;
  viewHistoryButton?: React.ReactNode;
  expiryDateSection?: React.ReactNode;
};

const ListingDetails: React.FunctionComponent<ListingDetailsProps> = ({
  listing,
  viewHistoryButton,
  expiryDateSection,
}) => {
  const { getLabel } = React.useContext(ListingLabelContext);
  const displayedField =
    getLabel('Neighborhood Display')?.trim().toUpperCase() === 'COUNTY'
      ? 'county'
      : 'neighborhood';

  return (
    <div className={'col-md-3 listing-details-component-details'}>
      <Text strong>Listing Details:</Text>
      {viewHistoryButton}

      <table className="table" style={{ width: '100%' }}>
        <tbody>
          {listing.status && (
            <tr>
              <td className="table-key">{getLabel('Status')}:</td>
              <td>{getLabel(listing.status)}</td>
            </tr>
          )}
          {listing.availability && (
            <tr>
              <td className="table-key">{getLabel('Availability')}:</td>
              <td>
                {getLabel(listing.availability)}
                {listing.isHistorical && (
                  <span style={{ color: 'red' }}> - Historical</span>
                )}
              </td>
            </tr>
          )}
          {(listing.list_price_type || listing.subtype) && (
            <tr>
              <td className="table-key">Transaction Type:</td>
              <td>
                {listing.list_price_type ||
                  `For ${capitalize(listing.subtype)}`}
              </td>
            </tr>
          )}
          {listing.status === 'MLS' && (
            <tr>
              <td className="table-key">MLS Number:</td>
              <td>
                {listing.combinedWith ? (
                  <a href={`/#/listing/${listing.combinedWith}/view`}>
                    {listing.mls_numbers_formatted}
                  </a>
                ) : (
                  listing.mls_numbers_formatted
                )}
              </td>
            </tr>
          )}
          {listing.status === 'Coming Soon' && (
            <tr>
              <td className="table-key">Coming Soon:</td>
              <td>
                {formatDateStrWithYearShort(
                  listing.coming_soon_date,
                  listing.timezone,
                )}
              </td>
            </tr>
          )}
          {listing.possession && (
            <tr>
              <td className="table-key">Possession:</td>
              <td>{listing.possession}</td>
            </tr>
          )}
          {listing.occupancy && (
            <tr>
              <td className="table-key">Occupancy:</td>
              <td>{listing.occupancy}</td>
            </tr>
          )}
          <tr>
            <td className="table-key">Listed Price:</td>
            <td>{formatPrice(listing.list_price, listing.type)}</td>
          </tr>
          {listing.sold_details && listing.availability === 'Sold' && (
            <>
              <tr>
                <td className="table-key">Sold Price:</td>
                <td>{formatPrice(listing.sold_details.price, listing.type)}</td>
              </tr>
              <tr>
                <td className="table-key">Sold Date:</td>
                <td>
                  {formatDateStrWithYearShort(
                    listing.sold_details.sold_date,
                    listing.timezone,
                  )}
                </td>
              </tr>
              <tr>
                <td className="table-key">Closing Date:</td>
                <td>
                  {formatDateStrWithYearShort(
                    listing.sold_details.closed_date,
                    listing.timezone,
                  )}
                </td>
              </tr>
            </>
          )}
          {listing.leased_details && listing.availability === 'Leased' && (
            <tr>
              <td className="table-key">Leased Price:</td>
              <td>{formatPrice(listing.leased_details.price, listing.type)}</td>
            </tr>
          )}
          {listing.condo?.maintenance && (
            <tr>
              <td className="table-key">Maintenance:</td>
              <td>
                {Number(listing.condo.maintenance).toLocaleString('us', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </td>
            </tr>
          )}
          {listing.taxes > 0 && (
            <tr>
              <td className="table-key">Taxes:</td>
              <td>
                {listing.taxes.toLocaleString('us', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </td>
            </tr>
          )}
          {listing.legal_description && (
            <tr>
              <td className="table-key">Legal:</td>
              <td>{listing.legal_description}</td>
            </tr>
          )}
          <tr>
            <td className="table-key">Area:</td>
            <td>
              {listing[displayedField]}
              <br />
              {listing.district}
              <br />
              {listing.dir_cross}
            </td>
          </tr>
          {expiryDateSection}
        </tbody>
      </table>
    </div>
  );
};

export default ListingDetails;
